import { useEffect, useState, useCallback } from 'react';
import { api } from '../api/client';

const LEVELS = [
  { value: 'NON_EVALUE',  label: '–',           color: '#94a3b8' },
  { value: 'INSUFFISANT', label: 'Insuffisant',  color: '#ef4444' },
  { value: 'EN_COURS',    label: 'En cours',     color: '#f59e0b' },
  { value: 'ACQUIS',      label: 'Acquis',       color: '#22c55e' },
  { value: 'MAITRISE',    label: 'Maîtrisé',     color: '#6366f1' },
];

const LEVEL_MAP = Object.fromEntries(LEVELS.map((l) => [l.value, l]));

function LevelBadge({ value }) {
  const l = LEVEL_MAP[value] || LEVEL_MAP['NON_EVALUE'];
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: 11, fontWeight: 600, color: '#fff', background: l.color,
    }}>{l.label}</span>
  );
}

// ── Onglet 1 : Référentiel ────────────────────────────────────────────────────
function TabReferentiel({ subjects }) {
  const [competences, setCompetences] = useState([]);
  const [filterSubject, setFilterSubject] = useState('');
  const [form, setForm] = useState(null); // null | { id?, name, subjectId, code, description, color, order }
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    const url = filterSubject ? `/competences?subjectId=${filterSubject}` : '/competences';
    api.get(url).then((d) => setCompetences(d || [])).catch((e) => setError(e.message));
  }, [filterSubject]);

  useEffect(() => { load(); }, [load]);

  async function save() {
    if (!form?.name?.trim()) return;
    setSaving(true);
    setError('');
    try {
      if (form.id) {
        await api.patch(`/competences/${form.id}`, form);
      } else {
        await api.post('/competences', form);
      }
      setForm(null);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function archive(id) {
    if (!confirm('Archiver cette compétence ?')) return;
    try {
      await api.delete(`/competences/${id}`);
      load();
    } catch (e) {
      setError(e.message);
    }
  }

  const grouped = competences.reduce((acc, c) => {
    const key = c.subject?.name || 'Sans matière';
    if (!acc[key]) acc[key] = [];
    acc[key].push(c);
    return acc;
  }, {});

  return (
    <div>
      {error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <select value={filterSubject} onChange={(e) => setFilterSubject(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13 }}>
          <option value="">Toutes les matières</option>
          {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <button onClick={() => setForm({ name: '', subjectId: '', code: '', description: '', color: '#6366f1', order: 0 })}
          style={{ marginLeft: 'auto', padding: '7px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
          + Nouvelle compétence
        </button>
      </div>

      {/* Modal form */}
      {form && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 28, width: 420, boxShadow: '0 8px 32px rgba(0,0,0,.2)' }}>
            <h3 style={{ margin: '0 0 18px', fontSize: 16 }}>{form.id ? 'Modifier' : 'Nouvelle'} compétence</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input placeholder="Nom *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }} />
              <select value={form.subjectId || ''} onChange={(e) => setForm({ ...form, subjectId: e.target.value })}
                style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
                <option value="">Sans matière</option>
                {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <input placeholder="Code (ex: C1)" value={form.code || ''} onChange={(e) => setForm({ ...form, code: e.target.value })}
                style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }} />
              <textarea placeholder="Description" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2} style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, resize: 'vertical' }} />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <label style={{ fontSize: 13, color: '#64748b' }}>Couleur :</label>
                <input type="color" value={form.color || '#6366f1'} onChange={(e) => setForm({ ...form, color: e.target.value })}
                  style={{ width: 36, height: 28, border: 'none', borderRadius: 4, cursor: 'pointer' }} />
                <input type="number" placeholder="Ordre" value={form.order ?? 0} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                  style={{ width: 70, padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
              <button onClick={() => setForm(null)} style={{ padding: '7px 14px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13 }}>Annuler</button>
              <button onClick={save} disabled={saving} style={{ padding: '7px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Liste groupée */}
      {Object.keys(grouped).length === 0
        ? <div style={{ textAlign: 'center', color: '#94a3b8', padding: 40 }}>Aucune compétence</div>
        : Object.entries(grouped).map(([subj, items]) => (
          <div key={subj} style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#475569', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>{subj}</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', border: '1px solid #e2e8f0', color: '#64748b', fontWeight: 600 }}>Code</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', border: '1px solid #e2e8f0', color: '#64748b', fontWeight: 600 }}>Nom</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', border: '1px solid #e2e8f0', color: '#64748b', fontWeight: 600 }}>Description</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', border: '1px solid #e2e8f0', color: '#64748b', fontWeight: 600 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} style={{ background: '#fff' }}>
                    <td style={{ padding: '7px 10px', border: '1px solid #e2e8f0' }}>
                      {c.code && <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 4, background: c.color || '#6366f1', color: '#fff', fontSize: 11, fontWeight: 700 }}>{c.code}</span>}
                    </td>
                    <td style={{ padding: '7px 10px', border: '1px solid #e2e8f0', fontWeight: 500 }}>{c.name}</td>
                    <td style={{ padding: '7px 10px', border: '1px solid #e2e8f0', color: '#64748b' }}>{c.description || '–'}</td>
                    <td style={{ padding: '7px 10px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                      <button onClick={() => setForm({ ...c })} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, marginRight: 8 }} title="Modifier">✏️</button>
                      <button onClick={() => archive(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }} title="Archiver">🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      }
    </div>
  );
}

// ── Onglet 2 : Évaluation par classe ─────────────────────────────────────────
function TabGrille({ classes, periods }) {
  const [classId, setClassId] = useState('');
  const [periodId, setPeriodId] = useState('');
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState('');

  async function loadGrid() {
    if (!classId || !periodId) return;
    setError('');
    try {
      const d = await api.get(`/competences/grille?classId=${classId}&periodId=${periodId}`);
      setData(d);
    } catch (e) {
      setError(e.message);
    }
  }

  useEffect(() => { if (classId && periodId) loadGrid(); }, [classId, periodId]);

  async function changeLevel(competenceId, studentId, level) {
    const key = `${competenceId}-${studentId}`;
    setSaving((p) => ({ ...p, [key]: true }));
    try {
      await api.post('/competences/results', { competenceId, studentId, periodId, level });
      setData((prev) => {
        const next = { ...prev };
        next.resultMap = { ...next.resultMap };
        if (!next.resultMap[competenceId]) next.resultMap[competenceId] = {};
        next.resultMap[competenceId] = { ...next.resultMap[competenceId], [studentId]: level };
        return next;
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving((p) => ({ ...p, [key]: false }));
    }
  }

  const LEVEL_OPTS = LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>);

  return (
    <div>
      {error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <select value={classId} onChange={(e) => setClassId(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
          <option value="">Choisir une classe</option>
          {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={periodId} onChange={(e) => setPeriodId(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
          <option value="">Choisir une période</option>
          {periods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {!data && <div style={{ textAlign: 'center', color: '#94a3b8', padding: 40 }}>Sélectionnez une classe et une période</div>}

      {data && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: 600 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: '8px 10px', border: '1px solid #e2e8f0', textAlign: 'left', position: 'sticky', left: 0, background: '#f8fafc', minWidth: 140, fontWeight: 600, color: '#475569' }}>Élève</th>
                {data.competences.map((c) => (
                  <th key={c.id} style={{ padding: '8px 6px', border: '1px solid #e2e8f0', textAlign: 'center', minWidth: 90, fontWeight: 600, color: '#475569' }}>
                    <div>{c.code && <span style={{ background: c.color || '#6366f1', color: '#fff', borderRadius: 3, padding: '1px 5px', fontSize: 10 }}>{c.code}</span>}</div>
                    <div style={{ fontSize: 11, marginTop: 2 }}>{c.name}</div>
                    {c.subject && <div style={{ fontSize: 10, color: '#94a3b8' }}>{c.subject.name}</div>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.students.map((s, i) => (
                <tr key={s.id} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={{ padding: '6px 10px', border: '1px solid #e2e8f0', position: 'sticky', left: 0, background: i % 2 === 0 ? '#fff' : '#f8fafc', fontWeight: 500 }}>
                    {s.lastName} {s.firstName}
                  </td>
                  {data.competences.map((c) => {
                    const key = `${c.id}-${s.id}`;
                    const val = data.resultMap[c.id]?.[s.id] || 'NON_EVALUE';
                    const lvl = LEVEL_MAP[val];
                    return (
                      <td key={c.id} style={{ padding: '4px 6px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                        <select
                          value={val}
                          disabled={saving[key]}
                          onChange={(e) => changeLevel(c.id, s.id, e.target.value)}
                          style={{
                            border: 'none', borderRadius: 4, padding: '3px 4px',
                            background: lvl?.color || '#94a3b8', color: '#fff',
                            fontWeight: 600, fontSize: 11, cursor: 'pointer',
                            opacity: saving[key] ? .5 : 1,
                          }}
                        >
                          {LEVEL_OPTS}
                        </select>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Export PDF ────────────────────────────────────────────────────────────────
function exportPDF(student, grouped, periodLabel) {
  const LEVEL_STYLES = {
    NON_EVALUE:  { bg: '#94a3b8', label: 'Non évalué' },
    INSUFFISANT: { bg: '#ef4444', label: 'Insuffisant' },
    EN_COURS:    { bg: '#f59e0b', label: 'En cours' },
    ACQUIS:      { bg: '#22c55e', label: 'Acquis' },
    MAITRISE:    { bg: '#6366f1', label: 'Maîtrisé' },
  };

  const subjectsHTML = Object.entries(grouped).map(([subj, comps]) => `
    <div class="subject">
      <div class="subject-title">${subj}</div>
      ${Object.entries(comps).map(([compName, { competence, periods: pResults }]) => `
        <div class="competence">
          <div class="comp-name">
            ${competence?.code ? `<span class="code" style="background:${competence.color || '#6366f1'}">${competence.code}</span>` : ''}
            ${compName}
          </div>
          <div class="periods">
            ${pResults.map((r) => {
              const st = LEVEL_STYLES[r.level] || LEVEL_STYLES.NON_EVALUE;
              return `<div class="period-block">
                <div class="period-name">${r.period?.name || ''}</div>
                <span class="badge" style="background:${st.bg}">${st.label}</span>
              </div>`;
            }).join('')}
          </div>
        </div>
      `).join('')}
    </div>
  `).join('');

  const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">
  <title>Rapport de compétences — ${student.lastName} ${student.firstName}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #1e293b; padding: 24px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #6366f1; padding-bottom: 14px; margin-bottom: 20px; }
    .school { font-size: 18px; font-weight: 700; color: #6366f1; }
    .doc-title { font-size: 13px; color: #475569; margin-top: 4px; }
    .student-info { text-align: right; }
    .student-name { font-size: 16px; font-weight: 700; }
    .student-meta { font-size: 11px; color: #64748b; margin-top: 3px; }
    .period-label { font-size: 11px; color: #64748b; margin-top: 2px; }
    .subject { margin-bottom: 18px; }
    .subject-title { font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 1px; padding: 5px 0; border-bottom: 1px solid #e2e8f0; margin-bottom: 8px; }
    .competence { border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 12px; margin-bottom: 6px; }
    .comp-name { font-weight: 600; font-size: 12px; margin-bottom: 6px; display: flex; align-items: center; gap: 6px; }
    .code { color: #fff; border-radius: 3px; padding: 1px 6px; font-size: 10px; }
    .periods { display: flex; gap: 10px; flex-wrap: wrap; }
    .period-block { text-align: center; }
    .period-name { font-size: 10px; color: #94a3b8; margin-bottom: 3px; }
    .badge { color: #fff; border-radius: 4px; padding: 2px 8px; font-size: 10px; font-weight: 700; display: inline-block; }
    .footer { margin-top: 24px; border-top: 1px solid #e2e8f0; padding-top: 10px; font-size: 10px; color: #94a3b8; text-align: center; }
    @media print { body { padding: 12px; } }
  </style></head><body>
  <div class="header">
    <div>
      <div class="school">Schoolizy</div>
      <div class="doc-title">Rapport de compétences</div>
    </div>
    <div class="student-info">
      <div class="student-name">${student.lastName} ${student.firstName}</div>
      ${student.matricule ? `<div class="student-meta">Matricule : ${student.matricule}</div>` : ''}
      ${periodLabel ? `<div class="period-label">${periodLabel}</div>` : '<div class="period-label">Toutes les périodes</div>'}
    </div>
  </div>
  ${subjectsHTML || '<p style="color:#94a3b8;text-align:center;padding:20px">Aucun résultat enregistré</p>'}
  <div class="footer">Document généré le ${new Date().toLocaleDateString('fr-FR')}</div>
  <script>window.onload = () => { window.print(); }<\/script>
  </body></html>`;

  const w = window.open('', '_blank');
  if (w) { w.document.write(html); w.document.close(); }
}

// ── Onglet 3 : Progression élève ─────────────────────────────────────────────
function TabProgression({ students, periods }) {
  const [studentId, setStudentId] = useState('');
  const [periodId, setPeriodId] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const filteredStudents = students.filter((s) =>
    !search || `${s.lastName} ${s.firstName} ${s.matricule || ''}`.toLowerCase().includes(search.toLowerCase())
  );

  async function load() {
    if (!studentId) return;
    setError('');
    try {
      const url = `/competences/progression/${studentId}${periodId ? `?periodId=${periodId}` : ''}`;
      const d = await api.get(url);
      setData(d);
    } catch (e) {
      setError(e.message);
    }
  }

  useEffect(() => { if (studentId) load(); }, [studentId, periodId]);

  // Grouper les résultats par matière > compétence > période
  const grouped = data?.results?.reduce((acc, r) => {
    const subj = r.competence?.subject?.name || 'Sans matière';
    const compName = r.competence?.name || '?';
    if (!acc[subj]) acc[subj] = {};
    if (!acc[subj][compName]) acc[subj][compName] = { competence: r.competence, periods: [] };
    acc[subj][compName].periods.push(r);
    return acc;
  }, {});

  return (
    <div>
      {error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative' }}>
          <input placeholder="Rechercher un élève..." value={search} onChange={(e) => setSearch(e.target.value)}
            style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, width: 200 }} />
          {search && (
            <div style={{ position: 'absolute', top: '100%', left: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,.1)', zIndex: 10, minWidth: 220, maxHeight: 200, overflowY: 'auto' }}>
              {filteredStudents.slice(0, 20).map((s) => (
                <div key={s.id} onClick={() => { setStudentId(s.id); setSearch(`${s.lastName} ${s.firstName}`); }}
                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f1f5f9' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}>
                  {s.lastName} {s.firstName} {s.matricule ? <span style={{ color: '#94a3b8', fontSize: 11 }}>({s.matricule})</span> : ''}
                </div>
              ))}
            </div>
          )}
        </div>
        <select value={periodId} onChange={(e) => setPeriodId(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
          <option value="">Toutes les périodes</option>
          {periods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {!studentId && <div style={{ textAlign: 'center', color: '#94a3b8', padding: 40 }}>Recherchez et sélectionnez un élève</div>}

      {data && grouped && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>
              {data.student.lastName} {data.student.firstName}
              {data.student.matricule && <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>({data.student.matricule})</span>}
            </div>
            <button
              onClick={() => {
                const periodLabel = periods.find((p) => p.id === periodId)?.name;
                exportPDF(data.student, grouped, periodLabel);
              }}
              style={{ padding: '7px 14px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              ⬇ Télécharger PDF
            </button>
          </div>

          {Object.keys(grouped).length === 0
            ? <div style={{ textAlign: 'center', color: '#94a3b8', padding: 20 }}>Aucun résultat enregistré</div>
            : Object.entries(grouped).map(([subj, comps]) => (
              <div key={subj} style={{ marginBottom: 24 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#475569', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{subj}</div>
                {Object.entries(comps).map(([compName, { competence, periods: pResults }]) => (
                  <div key={compName} style={{ marginBottom: 8, padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
                      {competence?.code && (
                        <span style={{ background: competence?.color || '#6366f1', color: '#fff', borderRadius: 4, padding: '1px 7px', fontSize: 11, marginRight: 8 }}>{competence.code}</span>
                      )}
                      {compName}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {pResults.map((r) => (
                        <div key={r.id} style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3 }}>{r.period?.name}</div>
                          <LevelBadge value={r.level} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────
export default function Competences() {
  const [tab, setTab] = useState(0);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [students, setStudents] = useState([]);

  useEffect(() => {
    api.get('/catalog/subjects').then((d) => setSubjects(d || [])).catch(() => {});
    api.get('/catalog/classes').then((d) => setClasses(d || [])).catch(() => {});
    api.get('/catalog/periods').then((d) => setPeriods(d || [])).catch(() => {});
    api.get('/students?limit=500').then((d) => setStudents(d?.data || d || [])).catch(() => {});
  }, []);

  const TABS = ['Référentiel', 'Évaluation par classe', 'Progression élève'];

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6, color: '#1e293b' }}>Compétences & Objectifs</h1>
      <p style={{ color: '#64748b', fontSize: 14, marginBottom: 24 }}>Gérez le référentiel de compétences et suivez la progression des élèves</p>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0', marginBottom: 24 }}>
        {TABS.map((t, i) => (
          <button key={i} onClick={() => setTab(i)}
            style={{
              padding: '9px 20px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              borderBottom: tab === i ? '2px solid #6366f1' : '2px solid transparent',
              marginBottom: -2, color: tab === i ? '#6366f1' : '#64748b',
              background: 'transparent', transition: 'all .15s',
            }}>
            {t}
          </button>
        ))}
      </div>

      {tab === 0 && <TabReferentiel subjects={subjects} />}
      {tab === 1 && <TabGrille classes={classes} periods={periods} />}
      {tab === 2 && <TabProgression students={students} periods={periods} />}
    </div>
  );
}
